"use client";

import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PhoneCall, ClipboardCheck, HeartPulse, Brain, Activity, ArrowRight } from "lucide-react";
const steps = [{
  id: "01",
  title: "Discovery Call",
  description: "Begin with a complimentary consultation to discuss your health history and specific wellness goals.",
  icon: PhoneCall
}, {
  id: "02",
  title: "Clinical Review",
  description: "Our medical experts perform a detailed evaluation to craft your bespoke, evidence-based care plan.",
  icon: ClipboardCheck
}, {
  id: "03",
  title: "Active Therapy",
  description: "Experience your personalized treatment sessions in our serene, medically-supervised environment.",
  icon: HeartPulse
}, {
  id: "04",
  title: "Guided Recovery",
  description: "Receive immediate post-session support and therapeutic tools to integrate your progress effectively.",
  icon: Brain
}, {
  id: "05",
  title: "Long-term Care",
  description: "Access ongoing check-ins and wellness resources to sustain your transformative health results.",
  icon: Activity
}];
export default function TheRainfallPatientJourney() {
  const sectionRef = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const start = windowHeight * 0.8;
      const end = windowHeight * 0.2;
      const totalDistance = rect.height + start - end;
      const currentDistance = start - rect.top;
      let percentage = currentDistance / totalDistance * 100;
      percentage = Math.max(0, Math.min(100, percentage));
      setProgress(percentage);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="102967" id="what-happens-during-your-session" ref={sectionRef} className="py-24 md:py-32 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[500px] bg-ring/10 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-foreground mb-6 tracking-tight">
            Your Journey to Vitality
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed font-sans">
            A seamless, clinical experience tailored to your unique health profile and long-term recovery goals.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="md:hidden absolute left-[27px] top-4 bottom-4 w-0.5 bg-muted rounded-full" />
          
          {}
          <div className="md:hidden absolute left-[27px] top-4 bottom-4 w-0.5 bg-ring rounded-full origin-top transition-transform duration-300 ease-out" style={{
          transform: `scaleY(${progress / 100})`
        }} />

          {}
          <div className="hidden md:block absolute left-0 right-0 top-[4.5rem] h-0.5 bg-muted rounded-full" />
          
          {}
          <div className="hidden md:block absolute left-0 right-0 top-[4.5rem] h-0.5 bg-ring rounded-full origin-left transition-transform duration-300 ease-out" style={{
          transform: `scaleX(${progress / 100})`
        }} />

          <div className="flex flex-col md:flex-row justify-between gap-12 md:gap-4 relative">
            {steps.map((step, i) => {
            const stepThreshold = i / (steps.length - 1) * 100;
            const isActive = progress >= stepThreshold - 10;
            return <div key={step.id} data-index={i} className="flex flex-row md:flex-col items-start md:items-center gap-6 md:gap-4 flex-1 relative group">
                  {}
                  <div className="hidden md:flex h-12 items-end justify-center mb-2">
                    <step.icon className={`size-10 transition-colors duration-500 ${isActive ? "text-ring" : "text-muted-foreground/30"}`} strokeWidth={1.25} />
                  </div>

                  {}
                  <div className="relative z-10 flex-shrink-0 bg-background py-2 md:py-0 md:px-4">
                    <Badge variant={isActive ? "default" : "outline"} className={`transition-all duration-500 text-sm px-4 py-1.5 rounded-full ${isActive ? "bg-ring text-primary-foreground shadow-xl shadow-ring/30 scale-110" : "bg-background text-muted-foreground border-border"}`}>
                      {step.id}
                    </Badge>
                  </div>

                  {}
                  <div className="flex flex-col gap-2 pt-1 md:pt-4 md:text-center">
                    <div className="md:hidden flex items-center gap-3 mb-1">
                      <step.icon className={`size-6 transition-colors duration-500 ${isActive ? "text-ring" : "text-muted-foreground/30"}`} />
                      <h3 className={`text-2xl font-serif transition-colors duration-500 ${isActive ? "text-foreground" : "text-foreground/60"}`}>
                        {step.title}
                      </h3>
                    </div>
                    
                    <h3 className={`hidden md:block text-2xl font-serif transition-colors duration-500 ${isActive ? "text-foreground" : "text-foreground/60"}`}>
                      {step.title}
                    </h3>
                    
                    <p className="text-base text-muted-foreground leading-relaxed max-w-[280px] md:mx-auto font-sans">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-20 md:mt-32 flex justify-center">
          <Button asChild size="lg" className="rounded-full px-10 h-16 text-lg shadow-2xl shadow-primary/20 bg-primary text-primary-foreground hover:bg-primary/90 transition-all">
            <Link to="/ContactPage">
              Start Your Consultation
              <ArrowRight className="ml-2 size-6" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}